import { useStripe, useElements, CardNumberElement } from "@stripe/react-stripe-js";
import { apiCompleteFreeOrder, apiCompleteOrderForIntent, apiCompletePaypalOrder, apiCreatePaymentIntent, apiUpdateUserAddress, } from "Root/api/apiCheckout";
import { setEditPaymentMethod } from "Root/checkout/redux/slices/checkoutComponentSlice";
import toastService from "Root/profile/toastService";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import posthog from "posthog-js";
import { setGlobalLoading } from "Root/profile/redux/actionCreators/makeActionCreator";
import { setCardData } from "Root/checkout/redux/slices/checkoutBillingSlice";
const setCookie = (name, value, days = 7, path = "/") => {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = name + "=" + encodeURIComponent(value) + "; expires=" + expires + "; path=" + path;
};
export const setOrderDataToLocalStorage = ({ shippingAddress, pricing, items, email }) => {
    const getShopstore = localStorage.getItem("shop-store-v1");
    const shopstore = getShopstore ? JSON.parse(getShopstore) : { state: {} };
    setCookie("byb-has-ordered", "1", 1);
    shopstore.state.orderData = {
        email,
        address: {
            address: shippingAddress.street,
            address2: shippingAddress.apartment,
            city: shippingAddress.city,
            state: shippingAddress.state,
            zip: shippingAddress.zip,
            firstName: shippingAddress.firstName,
            lastName: shippingAddress.lastName,
        },
    };
    shopstore.state.otbPricing = pricing;
    shopstore.state.plan = "otb";
    shopstore.state.order = items.map((item) => ({
        customizations: item.flavor_attribute_description,
        category: item.item_category,
        image: item.image_url,
        type: item.item_type,
        title: item.title,
        quantity: item.quantity,
        prices: {
            otb_unit_prices: {
                1: Number(item.otb_gross_price.replace("$", "")) * 100,
                2: Number(item.otb_gross_price.replace("$", "")) * 50,
            },
            otb_discounted_unit_prices: {
                1: Number(item.otb_net_price.replace("$", "")) * 100,
                2: Number(item.otb_net_price.replace("$", "")) * 50,
            }
        },
        plan: "otb",
    }));
    localStorage.setItem("shop-store-v1", JSON.stringify(shopstore));
};
const useCheckoutConfirmPayment = () => {
    // hooks
    const [showBilling, setShowBilling] = useState(false);
    const [isProp65Shown, setIsProp65Shown] = useState(false);
    const [isProp65ModalShown, setIsProp65ModalShown] = useState(false);
    const shippingData = useSelector((state) => state.checkoutShipping.address);
    const userCart = useSelector((state) => state.checkoutUserCart.userCart);
    const billingData = useSelector((state) => state.checkoutBilling.address);
    const cardData = useSelector((state) => state.checkoutBilling.cardData);
    const email = useSelector((state) => state.user.email);
    const pricingData = useSelector((state) => state.checkoutPricing);
    const { clientSecret } = pricingData;
    const componentState = useSelector((state) => state.checkoutComponent);
    const dispatch = useDispatch();
    const [cardComplete, setCardComplete] = useState({
        cardNumber: false,
        cardExpiry: false,
        cardCvc: false,
    });
    const [cardErrors, setCardErrors] = useState({
        cardNumber: "",
        cardExpiry: "",
        cardCvc: "",
    });
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState("");
    const stripe = useStripe();
    const elements = useElements();
    const { isInfluencer } = componentState;
    // methods
    const handleCardElementOnChange = (e) => {
        setCardComplete({ ...cardComplete, [e.elementType]: e.complete });
        dispatch(setCardData({ ...cardData, brand: cardData.brand || e?.brand || "" }));
        if (e.error) {
            setCardErrors({ ...cardErrors, [e.elementType]: e.error.message });
        }
    };
    const discount = pricingData.plan === "otb" ? pricingData.otb.discount : pricingData.subscription.discount;
    const getPropsForReviewBox = () => {
        return {
            onEdit: () => {
                dispatch(setEditPaymentMethod(true));
            },
            data: [
                {
                    title: "Payment Method",
                    text: [
                        `${cardData?.brand?.toLowerCase() === "paypal" ? "" : "Card "}`,
                        `${cardData.brand.toLowerCase() !== "unknown" ? cardData.brand : ""}`,
                        // giftCard ? `Gift Card (${giftCard.line_item_price})` : "",
                        discount && pricingData.plan !== "otb"
                            ? `Discount Code ${pricingData.subscription.promo_code[0] ? pricingData.subscription.promo_code[0].toUpperCase() : ""}`
                            : "",
                    ],
                },
                {
                    title: "Billing Address",
                    text: !showBilling
                        ? ["Same as shipping address"]
                        : [
                            `${billingData.firstName} ${billingData.lastName}`,
                            `${billingData.street}`,
                            `${billingData.apartment || ""}`,
                            `${billingData.city}, ${billingData.state} ${billingData.zip}`,
                        ],
                },
            ],
        };
    };
    const completeFreeOrder = async () => {
        dispatch(setGlobalLoading(true));
        try {
            await apiCompleteFreeOrder();
            toastService.successMessage(`Order successfully placed!`);
            if (pricingData.plan === "otb") {
                setOrderDataToLocalStorage({
                    shippingAddress: shippingData,
                    pricing: pricingData.otb,
                    items: userCart,
                    email,
                });
                window.location.href = "/logout?next=/shop/thank-you/";
            }
            window.location.href = "/app/thank-you/";
        }
        catch (err) {
            toastService.errorMessage("Something went wrong");
        }
    };
    const reviewOrder = async () => {
        window.scrollTo(0, 0);
        try {
            dispatch(setGlobalLoading(true));
            if (showBilling) {
                const billingAddress = {
                    name: `${billingData.firstName} ${billingData.lastName}`,
                    address: billingData.street,
                    address2: billingData.apartment,
                    city: billingData.city,
                    state: billingData.state,
                    zip: billingData.zip,
                    type: "billingaddress",
                };
                await apiUpdateUserAddress({ ...billingAddress, verify: false });
            }
            dispatch(setEditPaymentMethod(false));
        }
        catch (err) {
            toastService.errorMessage("Something went wrong");
        }
        finally {
            dispatch(setGlobalLoading(false));
        }
    };
    const showError = (payload) => {
        setError(payload.error.message || "");
        toastService.errorMessage(payload.error.message || "");
        dispatch(setEditPaymentMethod(true));
        dispatch(setGlobalLoading(false));
    };
    const completeOrderForIntent = async (payload) => {
        setError("");
        try {
            await apiCompleteOrderForIntent({
                shippingAddress: JSON.stringify({}),
                shippingSpeed: shippingData.shipping_method,
                payment_intent_id: pricingData.subscription.total > 0 && payload ? payload?.paymentIntent.id : undefined,
                isStarterKit: false,
                otb: pricingData.plan === "otb",
            });
            posthog?.capture("place_order", {
                purchase_type: pricingData.plan,
                order_class: "quiz",
            });
            if (pricingData.plan === "otb") {
                setOrderDataToLocalStorage({
                    shippingAddress: shippingData,
                    pricing: pricingData.otb,
                    items: userCart,
                    email,
                });
                window.location.href = "/logout?next=/shop/thank-you/";
                return;
            }
            window.location.href = "/app/thank-you/";
        }
        catch (err) {
            toastService.errorMessage("Something went wrong");
        }
    };
    const completePayment = async (passedClientSecret = "") => {
        if (isInfluencer) {
            completeFreeOrder();
            return;
        }
        let stripeClientSecret = passedClientSecret || clientSecret;
        if (!stripeClientSecret) {
            // if not set by this point we need to re-fetch
            dispatch(setGlobalLoading(true));
            const intentData = await apiCreatePaymentIntent({
                shippingAddress: "{}",
                shippingSpeed: shippingData.shipping_method,
                isStarterKit: false,
                otb: pricingData.plan === "otb",
            });
            stripeClientSecret = intentData.clientSecret;
        }
        try {
            dispatch(setGlobalLoading(true));
            const card = elements && elements.getElement(CardNumberElement);
            if (stripe && card) {
                if (pricingData.subscription.total > 0) {
                    const payload = await stripe.confirmCardPayment(stripeClientSecret, {
                        payment_method: {
                            card,
                        },
                    });
                    if (payload && payload?.error) {
                        showError(payload);
                    }
                    else {
                        completeOrderForIntent(payload);
                    }
                }
                else {
                    const payload = await stripe.confirmCardSetup(clientSecret, {
                        payment_method: {
                            card,
                        },
                    });
                    if (payload && payload?.error) {
                        showError(payload);
                    }
                    else {
                        completeOrderForIntent();
                    }
                }
            }
        }
        catch (error) {
            setError("Please try again. You may need to refresh your browser.");
            dispatch(setGlobalLoading(false));
            console.log(error);
            posthog?.capture("checkout_error", {});
        }
    };
    const completeOrderWithPaypal = async () => {
        dispatch(setGlobalLoading(true));
        const data = {
            shippingSpeed: shippingData.shipping_method,
        };
        try {
            await apiCompletePaypalOrder(data);
            toastService.successMessage(`Order successfully placed!`);
            posthog?.capture("place_order", {
                purchase_type: pricingData.plan,
                order_class: "quiz",
            });
            if (pricingData.plan === "otb") {
                setOrderDataToLocalStorage({
                    shippingAddress: shippingData,
                    pricing: pricingData.otb,
                    items: userCart,
                    email,
                });
                window.location.href = "/logout?next=/shop/thank-you/";
                return;
            }
            window.location.href = "/app/thank-you/";
        }
        catch (err) {
            toastService.errorMessage("Something went wrong");
        }
    };
    const toggleProp65Warning = (value) => () => {
        setIsProp65ModalShown(value);
    };
    // computed
    const showForm = componentState.isEditPaymentMethod;
    const showReviewBox = !componentState.isEditPlanSelect &&
        !componentState.isEditPaymentMethod &&
        !componentState.isEditShippingAddress &&
        !componentState.isEditShippingMethod;
    const validateAddress = Object.keys(billingData)
        .filter((item) => item !== "apartment")
        .some((key) => !billingData[key]);
    const hasUncompletedCardFields = Object.values(cardComplete).some((item) => !item);
    useEffect(() => {
        const proteinSku = userCart.find((item) => item.item_type.toLowerCase().includes("protein"));
        if (shippingData.state.toLowerCase() === "ca" &&
            (proteinSku?.sku.toLowerCase() === "lv" || proteinSku?.sku.toLowerCase() === "gv")) {
            setIsProp65Shown(true);
        }
    }, [shippingData, userCart]);
    return {
        showForm,
        showReviewBox,
        shippingData,
        billingData,
        componentState,
        validateAddress,
        getPropsForReviewBox,
        error,
        setShowBilling,
        completePayment,
        showBilling,
        handleCardElementOnChange,
        hasUncompletedCardFields,
        processing,
        reviewOrder,
        isPaypal: cardData.isPaypal,
        setIsPaypal: (value) => dispatch(setCardData({ ...cardData, isPaypal: value })),
        completeOrderWithPaypal,
        cardErrors,
        isInfluencer,
        isProp65Shown,
        isProp65ModalShown,
        toggleProp65Warning,
        isOtb: pricingData.plan === "otb",
    };
};
export default useCheckoutConfirmPayment;
